import { type Environment, isLiveEnvironment } from '~/vendor/cx-web/domain-types';

/**
 * GTM/analytics segmentation key for the `<body data-template>` attribute: the pathname segments
 * joined by `_`, e.g. `/us/terms-and-conditions` → `us_terms-and-conditions`. Single source of truth
 * for every consumer of `GoogleTagManagerBody` (Remix atlas/product-plus + Astro atlas-astro/
 * myoddschecker), which all set this attribute identically.
 */
export function computeDataTemplate(pathname: string): string {
  return pathname.split('/').slice(1).join('_');
}

/**
 * GTM loads on the deployed environments (PROD/STG). Single source of truth for the `shouldRender`
 * env gate shared by `GoogleTagManagerHead` / `GoogleTagManagerBody` call sites (combine with
 * `!disableGtm` where the flag applies).
 *
 * LOCALHOST is opt-in via `allowLocalhost` (driven by the `ENABLE_LOCALHOST_GTM` env flag) so a
 * developer can load the real container and verify events fire — without every local run silently
 * sending analytics to the live GTM/GA property. TEST NEVER loads GTM (unit tests must stay inert),
 * regardless of the flag.
 */
export function shouldRenderGtm(
  environment: Environment | undefined,
  { allowLocalhost = false }: { allowLocalhost?: boolean } = {},
): boolean {
  if (isLiveEnvironment(environment)) {
    return true;
  }
  return allowLocalhost && environment === 'LOCALHOST';
}
